import { DefaultApi, TasksApi, PersonaGroupsApi, PersonasApi, BotsApi, BiasCalculationsApi, VideosApi, ExperimentsApi, NarrativesApi } from '../BackendApiClient/apis';
import { CreatePersonaGroupRequest, UpdatePersonaGroupRequest, CreatePersonaRequest, ExperimentCreate } from '../BackendApiClient/models';
import { Configuration } from '../BackendApiClient/runtime';

// Create API instances with empty basePath to use relative URLs (proxy)
const config = new Configuration({ basePath: '' });
const defaultApi = new DefaultApi(config);
const botsApi = new BotsApi(config);
const tasksApi = new TasksApi(config);
const personaGroupsApi = new PersonaGroupsApi(config);
const personasApi = new PersonasApi(config);
const biasCalculationsApi = new BiasCalculationsApi(config);
const narrativesApi = new NarrativesApi(config);
const videosApi = new VideosApi(config);
const experimentsApi = new ExperimentsApi(config);

interface UseBackendReturn {
  // Bot Status API methods
  getBotStatus(): Promise<any>;
  startBot(groupId: string, usersCount: number): Promise<any>;
  stopBot(groupId: string): Promise<any>;
  bulkStartBots(groupIdsToUsersCount: Record<number, number>): Promise<any>;
  bulkStopBots(groupIds: number[]): Promise<any>;
  getBotLogs(groupId: string): Promise<any>;

  // Bot Command & Control API methods
  getAllTasks(): Promise<any>;
  getAllTaskExecutions(): Promise<any>;
  createTask(taskData: any): Promise<any>;
  assignTask(taskData: any): Promise<any>;
  changeTaskStatus(taskData: any): Promise<any>;

  // Health check
  healthCheck(): Promise<any>;

  // Videos API methods
  uploadVideosProtobuf(data: any): Promise<any>;

  // Persona Groups API methods
  getPersonaGroups(includeUsers?: boolean): Promise<any>;
  getPersonaGroup(groupId: number): Promise<any>;
  createPersonaGroup(request: CreatePersonaGroupRequest): Promise<any>;
  updatePersonaGroup(groupId: number, request: UpdatePersonaGroupRequest): Promise<any>;
  deletePersonaGroup(groupId: number): Promise<any>;

  // Personas API methods  
  getPersonas(): Promise<any>;
  getPersona(userId: string): Promise<any>;
  createPersona(request: CreatePersonaRequest): Promise<any>;

  // Bias Calculations API methods
  getBiasCalculations(): Promise<any>;

  // Narrative Mappings API methods  
  getNarrativeMappings(): Promise<any>;

  // Experiments API methods
  getExperiments(): Promise<any>;
  getExperiment(experimentId: number): Promise<any>;
  createExperiment(request: ExperimentCreate): Promise<any>;
  deleteExperiment(experimentId: number): Promise<any>;
  addGroupsToExperiment(experimentId: number, groupIds: number[]): Promise<any>;
  removeGroupsFromExperiment(experimentId: number, groupIds: number[]): Promise<any>;
  getBiasTests(): Promise<any>;
}

export const useBackend = (): UseBackendReturn => {
  return {
    // Bot Status API methods
    async getBotStatus() {
      return botsApi.getBotsStatusApiBotsGet();
    },

    async startBot(groupId: string, usersCount: number = 1) {
      return botsApi.startBotApiBotsGroupIdStartPost({ 
        groupId: parseInt(groupId), 
        startBotRequest: { usersCount } 
      });
    },

    async stopBot(groupId: string) {
      return botsApi.stopBotApiBotsGroupIdStopPost({ groupId: parseInt(groupId) });
    },

    async bulkStartBots(groupIdsToUsersCount: Record<number, number>) {
      return botsApi.bulkStartBotsApiBotsBulkStartPost({
        bulkStartBotRequest: { groupIdsToUsersCount }
      });
    },

    async bulkStopBots(groupIds: number[]) {
      return botsApi.bulkStopBotsApiBotsBulkStopPost({
        bulkStopBotRequest: { groupIds }
      });
    },

    async getBotLogs(groupId: string) {
      return botsApi.getBotLogsApiBotsGroupIdLogsGet({ groupId: parseInt(groupId) });
    },

    // Bot Command & Control API methods
    async getAllTasks() {
      return tasksApi.getTasksApiTasksGet();
    },

    async getAllTaskExecutions() {
      return tasksApi.getExecutionsApiTasksExecutionsGet({ timeBackSeconds: 86400 });
    },

    async createTask(taskData: any) {
      return tasksApi.createTaskApiTasksPost({ inputTask: taskData });
    },

    async assignTask(taskData: any) {
      return tasksApi.assignTaskApiTasksAssignPost({ inputTaskExecution: taskData });
    },

    async changeTaskStatus(taskData: any) {
      return tasksApi.updateStatusApiTasksExecutionsStatusPost({ updateTaskExecutionRequest: taskData });
    },

    // Health check
    async healthCheck() {
      return defaultApi.healthCheckApiHealthGet();
    },

    // Videos API methods
    async uploadVideosProtobuf(data: any) {
      return videosApi.uploadFeedProtobufToS3ApiVideosFeedProtobufPost(data);
    },

    // Persona Groups API methods
    async getPersonaGroups(includeUsers?: boolean) {
      return personaGroupsApi.getPersonaGroupsApiPersonaGroupsGet({
        includeUsers: includeUsers
      });
    },

    async getPersonaGroup(groupId: number) {
      return personaGroupsApi.getPersonaGroupApiPersonaGroupsGroupIdGet({ groupId });
    },

    async createPersonaGroup(request: CreatePersonaGroupRequest) {
      return personaGroupsApi.createPersonaGroupApiPersonaGroupsPost({ createPersonaGroupRequest: request });
    },

    async updatePersonaGroup(groupId: number, request: UpdatePersonaGroupRequest) {
      return personaGroupsApi.updatePersonaGroupApiPersonaGroupsGroupIdPut({ groupId, updatePersonaGroupRequest: request });
    },

    async deletePersonaGroup(groupId: number) {
      return personaGroupsApi.deletePersonaGroupApiPersonaGroupsGroupIdDelete({ groupId });
    },

    // Personas API methods
    async getPersonas() {
      return personasApi.getPersonasApiPersonasGet({
        includeGroups: true,
        includeRegion: true,
        includeDevice: true,
        includeAppParameters: true,
      });
    },

    async getPersona(userId: string) {
      return personasApi.getPersonaApiPersonasUserIdGet({ userId });
    },

    async createPersona(request: CreatePersonaRequest) {
      return personasApi.createPersonaApiPersonasPost({ createPersonaRequest: request });
    },

    // Bias Calculations API methods
    async getBiasCalculations() {
      return biasCalculationsApi.getBiasCalculationsApiBiasCalculationsGet();
    },

    // Narrative Mappings API methods
    async getNarrativeMappings() {
      return narrativesApi.getNarrativeMappingsApiNarrativesMappingsGet();
    },

    // Experiments API methods
    async getExperiments() {
      return experimentsApi.listExperimentsApiExperimentsGet({});
    },

    async getExperiment(experimentId: number) {
      return experimentsApi.getExperimentApiExperimentsExperimentIdGet({ experimentId });
    },

    async createExperiment(request: ExperimentCreate) {
      return experimentsApi.createExperimentApiExperimentsPost({ experimentCreate: request });
    },

    async deleteExperiment(experimentId: number) {
      return experimentsApi.deleteExperimentApiExperimentsExperimentIdDelete({ experimentId });
    },

    async addGroupsToExperiment(experimentId: number, groupIds: number[]) {
      return experimentsApi.addGroupsToExperimentApiExperimentsExperimentIdGroupsPost({
        experimentId,
        addGroupsRequest: { groupIds },
      });
    },

    async removeGroupsFromExperiment(experimentId: number, groupIds: number[]) {
      return experimentsApi.removeGroupsFromExperimentApiExperimentsExperimentIdGroupsDelete({
        experimentId,
        removeGroupsRequest: { groupIds },
      });
    },

    async getBiasTests() {
      return experimentsApi.listBiasTestsApiExperimentsBiasTestsGet({});
    },
  };
};
